import React, { useEffect, useState } from 'react';

const DISMISS_KEY = 'pwaInstallPromptDismissed';

const getMobilePlatform = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  const userAgent = window.navigator.userAgent || window.navigator.vendor;
  const isAndroid = /android/i.test(userAgent);
  const isIOSDevice =
    /iPad|iPhone|iPod/.test(userAgent) ||
    ['iPhone', 'iPad', 'iPod'].includes(window.navigator.platform);

  if (isAndroid) {
    return 'android';
  }

  if (isIOSDevice) {
    return 'ios';
  }

  return null;
};

const isStandaloneMode = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  const matchMedia = window.matchMedia ? window.matchMedia('(display-mode: standalone)').matches : false;
  return matchMedia || window.navigator.standalone === true;
};

function PwaInstallPrompt() {
  const [platform, setPlatform] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [installEvent, setInstallEvent] = useState(null);

  useEffect(() => {
    const detectedPlatform = getMobilePlatform();
    let dismissed = false;

    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        dismissed = window.localStorage.getItem(DISMISS_KEY) === 'true';
      } catch (e) {
        // Ignore storage-related errors (e.g., disabled storage, private mode)
        dismissed = false;
      }
    }
    setPlatform(detectedPlatform);

    if (!detectedPlatform || dismissed || isStandaloneMode()) {
      setIsVisible(false);
      return undefined;
    }

    setIsVisible(true);

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setInstallEvent(event);
    };

    const handleAppInstalled = () => {
      setIsVisible(false);
      setInstallEvent(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, 'true');
    setIsVisible(false);
  };

  const handleInstallClick = async () => {
    if (!installEvent) {
      return;
    }

    installEvent.prompt();
    await installEvent.userChoice;

    // Hide the prompt after the user has made a choice (accepted or dismissed)
    setIsVisible(false);

    setInstallEvent(null);
  };

  if (!isVisible || !platform) {
    return null;
  }

  return (
    <div className="pwa-install-prompt" role="dialog" aria-live="polite" aria-label="Install app">
      <div className="pwa-install-header">
        <div className="pwa-install-title">Add WhistlerPoll to your home screen</div>
        <div className="pwa-install-subtitle">Get quick access without the browser.</div>
      </div>
      <div className="pwa-install-instructions">
        {platform === 'ios' ? (
          <ol>
            <li>Tap the Share icon in your browser.</li>
            <li>Select "Add to Home Screen".</li>
            <li>Confirm by tapping "Add".</li>
          </ol>
        ) : (
          <ol>
            {installEvent ? (
              <li>Tap "Install app" below.</li>
            ) : (
              <li>Open your browser menu.</li>
            )}
            <li>Select "Add to Home screen".</li>
            <li>Confirm the install.</li>
          </ol>
        )}
      </div>
      <div className="pwa-install-actions">
        {platform === 'android' && installEvent && (
          <button
            type="button"
            className="pwa-install-btn pwa-install-btn-primary"
            onClick={handleInstallClick}
          >
            Install app
          </button>
        )}
        <button
          type="button"
          className="pwa-install-btn pwa-install-btn-secondary"
          onClick={handleDismiss}
        >
          Do not show again
        </button>
      </div>
    </div>
  );
}

export default PwaInstallPrompt;
